/**
 * IndexedDB File Storage Manager
 * 
 * Single source of truth for all code file storage and management.
 * Replaces localStorage-based fileStorageManager for better scalability and performance.
 * 
 * Usage:
 * - Direct: import indexedDbFileStorage from './utils/indexedDbFileStorage'
 * - Unified: import storageManager from './utils/storageManager' (recommended)
 * 
 * Features:
 * - All file operations use IndexedDB
 * - No localStorage dependency
 * - Better quota handling (50MB+ vs 5MB)
 * - Atomic transactions
 * - Efficient querying with indexes
 * - State tracking (modified, sent, modified_after_sent)
 */

import { DEFAULT_MICROSERVICE_BRANCH, DEFAULT_PROJECT_BRANCH } from '../constants/gitConstants';
import commitMessageStorage from './commitMessageStorage';
import * as vfsClient from './vfsClient';
import { devLog } from './devLog';
import { fullPathOf, scopesContaining } from './cacheScopePaths';

/**
 * Tells every open file tree (this tab: a window event; other tabs: a BroadcastChannel) that cached files of these
 * scopes changed, so a tree showing one of them redraws. Detail: `{ projectId, scopes, paths }`.
 */
export const CACHE_SCOPES_CHANGED_EVENT = 'vfsCacheScopesChanged';
let cacheChannel = null;
export function announceCacheScopesChanged(detail, { local = true } = {}) {
  if (local && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CACHE_SCOPES_CHANGED_EVENT, { detail }));
  }
  try {
    if (!cacheChannel && typeof BroadcastChannel !== 'undefined') { cacheChannel = new BroadcastChannel('mia-flow-vfs-cache'); }
    cacheChannel?.postMessage(detail);
  } catch {
    // no cross-tab notice: the other tab catches up the next time its tree loads
  }
}

const DB_NAME = 'CodeFileStorage';
const DB_VERSION = 2;
const FILES_STORE = 'files';
const METADATA_STORE = 'metadata';

// Git lifecycle states (tracking relationship with GitLab)
const GIT_STATE = {
  ORIGINAL: 'original',    // File from GitLab, unmodified
  MODIFIED: 'modified',    // File has local changes not pushed to GitLab
  PUSHED: 'pushed',        // File has been pushed to GitLab
  NEW: 'new',             // New file not in GitLab yet
  DELETED: 'deleted'       // File marked for deletion
};

// Preview lifecycle states (tracking relationship with live preview)
const PREVIEW_STATE = {
  NOT_SENT: 'not_sent',    // File not sent to preview yet
  SENT: 'sent',            // File sent to preview and up-to-date
  OUTDATED: 'outdated'     // File sent to preview but modified since
};


/**
 * Files to EXCLUDE from being sent to AI - dependency lock files and generated files
 * that are not useful for coding agents
 */
const EXCLUDED_FILES_FOR_AI = [
  // JavaScript/Node.js
  'package-lock.json',
  'yarn.lock',
  'pnpm-lock.yaml',
  'npm-shrinkwrap.json',
  
  // Python
  'poetry.lock',
  'Pipfile.lock',
  'pdm.lock',
  'requirements.lock',
  
  // Ruby
  'Gemfile.lock',
  
  // PHP
  'composer.lock',
  
  // Rust
  'Cargo.lock',
  
  // Go
  'go.sum',
  
  // .NET
  'packages.lock.json',
  'paket.lock',
  
  // Java/JVM
  'gradle.lockfile',
  'gradlew',
  'gradlew.bat',
  'mvnw',
  'mvnw.cmd',
  
  // Swift
  'Package.resolved',
  
  // Dart/Flutter  
  'pubspec.lock',
  
  // Other generated/build files
  '.DS_Store',
  'Thumbs.db',
  '.env.local',
  '.env.*.local'
];

/**
 * Check if a file should be excluded from AI context
 * @param {string} filePath - Full file path
 * @returns {boolean} - True if file should be excluded
 */
function shouldExcludeFromAI(filePath) {
  const fileName = filePath.split('/').pop();
  return EXCLUDED_FILES_FOR_AI.includes(fileName);
}

/**
 * Get current tenant ID from localStorage
 * @returns {string|null}
 */
function getTenantId() {
  try {
    const stored = localStorage.getItem('mia_selectedTenant');
    if (stored) {
      const tenant = JSON.parse(stored);
      return tenant.tenantId || tenant.id || null;
    }
  } catch (error) {
    console.error('Failed to get tenant ID:', error); /* eslint-disable-line no-console */
  }
  return null;
}

class IndexedDbFileStorage {
  constructor() {
    this.db = null;
    this.isSupported = typeof indexedDB !== 'undefined';
    this.initPromise = null;
  }

  /**
   * Initialize the database connection with proper schema
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this.isSupported) {
      throw new Error('IndexedDB is not supported in this browser');
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error); /* eslint-disable-line no-console */
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        devLog('✅ IndexedDB file storage initialized'); /* eslint-disable-line no-console */
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create files store with indexes
        if (!db.objectStoreNames.contains(FILES_STORE)) {
          const filesStore = db.createObjectStore(FILES_STORE, {keyPath: 'id'});
          
          // Indexes for efficient querying
          filesStore.createIndex('projectId', 'projectId', {unique: false});
          filesStore.createIndex('serviceName', 'serviceName', {unique: false});
          filesStore.createIndex('filePath', 'filePath', {unique: false});
          filesStore.createIndex('state', 'state', {unique: false});
          filesStore.createIndex('lastModified', 'lastModified', {unique: false});
          filesStore.createIndex('projectService', ['projectId', 'serviceName'], {unique: false});
          filesStore.createIndex('fullPath', ['projectId', 'serviceName', 'filePath'], {unique: true});
          
          devLog('📦 Created files object store with indexes'); /* eslint-disable-line no-console */
        }

        // Create metadata store for project-level data
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          const metadataStore = db.createObjectStore(METADATA_STORE, {keyPath: 'key'});
          metadataStore.createIndex('projectId', 'projectId', {unique: false});
          devLog('📦 Created metadata object store'); /* eslint-disable-line no-console */
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate unique ID for file storage
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {string}
   */
  generateFileId(projectId, serviceName, filePath) {
    return `${projectId}::${serviceName}::${filePath}`;
  }


  /**
   * Save file content to IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @param {string} content
   * @param {object} options - Additional options (branch, originalContent, etc.)
   * @returns {Promise<boolean>}
   */
  async saveFile(projectId, serviceName, filePath, content, options = {}) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);
      
      // Get current file to determine state transition
      const currentFile = await this.getFile(projectId, serviceName, filePath);
      
      // Determine git and preview states separately
      let gitState, previewState;
      
      // Check if file is being pulled from Git, or from vfs-service (a vfs-backed
      // project's equivalent of a pull — see the write-through push below and
      // vfsClient.js's module doc) - both cases should always be ORIGINAL/clean,
      // never trigger the write-through push back (that would ping-pong).
      if (options.fromGit || options.fromVfs) {
        gitState = GIT_STATE.ORIGINAL;
        previewState = PREVIEW_STATE.NOT_SENT;
      } else if (currentFile) {
        // File exists - check if content changed
        const contentChanged = currentFile.content !== content;
        
        if (!contentChanged) {
          // Content hasn't changed - keep current states
          gitState = currentFile.gitState || this._migrateToGitState(currentFile.state);
          previewState = currentFile.previewState || this._migrateToPreviewState(currentFile.state);
        } else {
          // Content changed - update states
          
          // Git state: if file was pushed/original, mark as modified; keep NEW as NEW
          const currentGitState = currentFile.gitState || this._migrateToGitState(currentFile.state);
          if (currentGitState === GIT_STATE.PUSHED || currentGitState === GIT_STATE.ORIGINAL) {
            gitState = GIT_STATE.MODIFIED;
          } else if (currentGitState === GIT_STATE.NEW) {
            gitState = GIT_STATE.NEW;
          } else {
            gitState = GIT_STATE.MODIFIED;
          }
          
          // Preview state: if file was sent to preview, mark as outdated
          const currentPreviewState = currentFile.previewState || this._migrateToPreviewState(currentFile.state);
          if (currentPreviewState === PREVIEW_STATE.SENT) {
            previewState = PREVIEW_STATE.OUTDATED;
          } else {
            previewState = currentPreviewState;
          }
        }
      } else if (options.isNew) {
        gitState = GIT_STATE.NEW;
        previewState = PREVIEW_STATE.NOT_SENT;
      } else {
        // New file from GitLab - mark as ORIGINAL
        gitState = GIT_STATE.ORIGINAL;
        previewState = PREVIEW_STATE.NOT_SENT;
      }

      const fileData = {
        id,
        projectId,
        serviceName,
        filePath,
        content,
        gitState,
        previewState,
        lastModified: Date.now(),
        lastPushedToGitLab: currentFile?.lastPushedToGitLab || null,
        lastSentToPreview: currentFile?.lastSentToPreview || null,
        originalContent: options.originalContent || currentFile?.originalContent || content,
        branch: options.branch || currentFile?.branch || DEFAULT_MICROSERVICE_BRANCH,
        fileName: options.fileName || filePath.split('/').pop(),
        language: options.language || currentFile?.language,
        isBinary: options.isBinary || currentFile?.isBinary || false,
        mimeType: options.mimeType || currentFile?.mimeType,
        size: options.size || currentFile?.size,
        metadata: {
          ...currentFile?.metadata,
          ...options.metadata
        }
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(fileData);

        request.onsuccess = async () => {
          // Check if content actually changed before dispatching event
          const contentChanged = !currentFile || currentFile.content !== content;
          
          // eslint-disable-next-line no-console
          devLog(`💾 Saved file to IndexedDB: ${filePath} (state: ${gitState}, contentChanged: ${contentChanged})`);
          
          // Track commit message for file modifications
          if (contentChanged && gitState !== GIT_STATE.ORIGINAL) {
            try {
              const tenantId = getTenantId();
              const fileName = filePath.split('/').pop();
              const commitMessage = `Modified ${fileName}`;
              
              await commitMessageStorage.appendMessage(
                tenantId,
                projectId,
                serviceName,
                commitMessage
              );
              
              // eslint-disable-next-line no-console
              devLog(`📝 Appended commit message: ${commitMessage}`);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Failed to append commit message:', error);
              // Don't fail the file save if commit message tracking fails
            }
          }
          
          // Only dispatch event if content actually changed
          // This prevents infinite loops where the same content is saved repeatedly
          if (contentChanged) {
            // Dispatch event to notify Monaco editor and tree view of changes
            const event = new CustomEvent('monacoFileChanged', {
              detail: {
                projectId,
                serviceName,
                filePath,
                content,
                state: gitState
              }
            });
            window.dispatchEvent(event);
          }

          // Write-through to vfs-service for a vfs-backed project (created without
          // a git connection — see vfsClient.js). Fire-and-forget: this is a local-
          // first cache, so a network hiccup must not block or fail the local save.
          // Skipped for a pull (fromGit/fromVfs) to avoid pushing content vfs-service
          // just sent us right back at it. `serviceName` is prepended: vfs-service
          // stores every service's files under a `<serviceName>/` subfolder of the
          // project, but filePath here is service-relative (matching the git-backed
          // convention, where a repo's root already IS the service root) — see
          // vfsClient.fetchServiceFiles's doc for the read-side of this same mapping.
          //
          // patchFile (not writeFile) whenever a previous version is known: it
          // merges through vfs-service's live Yjs doc for the file, so any other
          // client with it open (vfs-fe, another tab, an agent) sees the edit in
          // real time — writeFile's plain PUT bypasses that layer entirely and
          // would leave any already-open viewer showing stale content until it
          // reopens the file. Only a genuinely new file (no currentFile) uses
          // writeFile, since there's nothing to diff against yet.
          //
          // `liveSynced`: the editor has this file bound to vfs-service's live doc (hooks/useCollaboration.js), which
          // already carries the edit and commits it under its author; a /patch on top would apply it a second time.
          if (contentChanged && !options.fromGit && !options.fromVfs && !options.liveSynced && vfsClient.isVfsBackedProject(projectId)) {
            const vfsPath = `${serviceName}/${filePath}`;
            const pushPromise = currentFile
              ? vfsClient.patchFile(projectId, vfsPath, currentFile.content, content)
              : vfsClient.writeFile(projectId, vfsPath, content);
            // Saved on vfs-service: the other trees' copies of this file (Home › Files and a service's code view
            // cache it under different scopes) get the new content too.
            pushPromise.then(() => this.mirrorToOverlappingScopes(projectId, serviceName, filePath, { content, isBinary: fileData.isBinary, mimeType: fileData.mimeType, size: fileData.size }));
            pushPromise.catch((error) => {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ vfs-service write-through failed for ${filePath} (kept locally in IndexedDB):`, error);
            });
          }

          if (contentChanged && (options.fromVfs || options.liveSynced) && !options.localOnly) {
            await this.mirrorToOverlappingScopes(projectId, serviceName, filePath, { content, isBinary: fileData.isBinary, mimeType: fileData.mimeType, size: fileData.size });
          }

          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to save file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error saving file:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get file content from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<object|null>}
   */
  async getFile(projectId, serviceName, filePath) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readonly');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.get(id);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          console.error('❌ Failed to get file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting file:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Get all files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getAllFiles(projectId, serviceName) {
    try {
      await this.init();

      // Validate parameters to prevent IndexedDB key errors
      // Check for undefined, null, empty string, or non-string types
      if (!projectId || !serviceName || typeof projectId !== 'string' || typeof serviceName !== 'string' || projectId.trim() === '' || serviceName.trim() === '') {
        console.warn('⚠️ getAllFiles called with invalid parameters:', { projectId, serviceName }); /* eslint-disable-line no-console */
        return [];
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readonly');
        const store = transaction.objectStore(FILES_STORE);
        const index = store.index('projectService');
        const request = index.getAll([projectId, serviceName]);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          console.error('❌ Failed to get all files:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting all files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get every file cached for a project, across every service — unlike
   * getAllFiles(), the caller doesn't need to already know which services
   * exist. Used by the legacy-project vfs backfill (vfsClient.js), which
   * has no other way to learn a project's service list since a legacy
   * project was never registered with the Console/BFF microservices API
   * the normal service-list flow depends on.
   * @param {string} projectId
   * @returns {Promise<Array>} raw file records (same shape as getAllFiles)
   */
  async getAllFilesForProject(projectId) {
    try {
      await this.init();

      if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
        console.warn('⚠️ getAllFilesForProject called with invalid projectId:', projectId); /* eslint-disable-line no-console */
        return [];
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readonly');
        const store = transaction.objectStore(FILES_STORE);
        const index = store.index('projectId');
        const request = index.getAll(IDBKeyRange.only(projectId));

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          console.error('❌ Failed to get all files for project:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting all files for project:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * One-time migration of a legacy (non-vfs-backed) project's cached files
   * into a real vfs-service project — see the control-plane-split
   * architecture note's "Full mia-flow-be decommission" section for why
   * this exists: mia-flow-be holds no server-side copy of a legacy
   * project's files at all (its canvas-file tool round-trips to whichever
   * browser has the project open), so the backfill can only run here, off
   * whatever THIS browser has cached. A legacy project never opened in
   * this browser can't be backfilled from here — nothing to migrate it
   * FROM.
   *
   * Idempotent and safe to call speculatively on every project load:
   * no-ops immediately if the project is already vfs-backed or if this
   * browser has nothing cached for it, and every vfs-service call it makes
   * (createProject, writeFile) already tolerates being repeated. Does NOT
   * flip the vfs-backed flag unless every file write succeeds — a partial
   * failure leaves the project on the legacy chat path so a later open can
   * retry, rather than silently switching to an incomplete vfs-service copy.
   *
   * Known limitation: writes every cached record's `content` as plain text
   * (`vfsClient.writeFile`, `content-type: text/plain`) — correct for every
   * path actually exercised in this codebase today, but would corrupt a
   * genuinely binary asset stored as raw (non-base64) bytes rather than text.
   *
   * @param {string} projectId
   * @returns {Promise<{migrated: boolean, fileCount: number, serviceCount: number, error: string|null}>}
   */
  async backfillLegacyProjectToVfs(projectId) {
    if (vfsClient.isVfsBackedProject(projectId)) {
      return {migrated: false, fileCount: 0, serviceCount: 0, error: null};
    }

    // BUG FIX (real data loss, live-confirmed): `isVfsBackedProject` above
    // is a client-side, localStorage-only flag — it has a real gap for a
    // BRAND-NEW project the agent just created this session: Canvas.jsx's
    // own `loadConfiguration` sets this flag too (as a side effect of its
    // own live vfs-service check), but only deep inside an async function,
    // well after this backfill effect (App.jsx, fired the instant
    // `canvasProjectContext.projectId` becomes truthy) can already have
    // run. The result: a genuinely, definitely vfs-backed project got
    // treated as a "legacy, never-vfs-backed" one and had this browser's
    // IndexedDB cache (from a DIFFERENT, stale service-key namespace, or
    // simply not-yet-fresh) pushed UP over vfs-service's real files —
    // live-confirmed as the cause of a fresh build's Preview showing the
    // real app while Code's Monaco showed the scaffold placeholder for
    // App.tsx, moments after the agent had written the real one. Do our
    // OWN authoritative live check here — the one Canvas.jsx's effect
    // would eventually do anyway — rather than trust a flag with a known
    // race. A project vfs-service already has ANY real content for is
    // never a legacy-migration candidate, full stop.
    try {
      const { isDirectory, entries } = await vfsClient.listDirectoryEntries(projectId, '.');
      if (isDirectory && entries?.length > 0) {
        vfsClient.markProjectVfsBacked(projectId);
        return {migrated: false, fileCount: 0, serviceCount: 0, error: null};
      }
    } catch {
      // Genuinely doesn't exist on vfs-service yet (404) or a transient
      // network error — either way, fall through to the existing
      // createProject-based path below, exactly as before this fix.
    }

    const records = await this.getAllFilesForProject(projectId);
    const liveRecords = records.filter(
      (record) => record.gitState !== GIT_STATE.DELETED && record.serviceName && record.filePath
    );

    if (liveRecords.length === 0) {
      // Nothing cached in this browser for this project — can't backfill from here.
      return {migrated: false, fileCount: 0, serviceCount: 0, error: null};
    }

    try {
      await vfsClient.createProject(projectId);

      const serviceNames = new Set();
      for (const record of liveRecords) {
        const vfsPath = `${record.serviceName}/${record.filePath}`;
        // eslint-disable-next-line no-await-in-loop -- sequential on purpose: a one-time background migration, not a hot path; avoids bursting vfs-service with concurrent writes for large projects.
        await vfsClient.writeFile(projectId, vfsPath, record.content ?? '');
        serviceNames.add(record.serviceName);
      }

      for (const serviceName of serviceNames) {
        vfsClient.registerVfsService(projectId, serviceName);
      }
      vfsClient.markProjectVfsBacked(projectId);

      devLog(`✅ Backfilled legacy project ${projectId} into vfs-service: ${liveRecords.length} file(s) across ${serviceNames.size} service(s)`); /* eslint-disable-line no-console */
      return {migrated: true, fileCount: liveRecords.length, serviceCount: serviceNames.size, error: null};
    } catch (error) {
      console.error(`❌ Failed to backfill legacy project ${projectId} into vfs-service:`, error); /* eslint-disable-line no-console */
      return {migrated: false, fileCount: 0, serviceCount: 0, error: error?.message || String(error)};
    }
  }

  /**
   * Get modified files (files that need to be pushed to GitLab)
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getModifiedFiles(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      const modifiedFiles = allFiles.filter(file => {
        const gitState = file.gitState || this._migrateToGitState(file.state);
        return gitState === GIT_STATE.MODIFIED || 
               gitState === GIT_STATE.NEW || 
               gitState === GIT_STATE.DELETED;
      });
      
      // Debug logging
      // eslint-disable-next-line no-console
      devLog(`🔍 getModifiedFiles(${projectId}, ${serviceName}):`, {
        totalFiles: allFiles.length,
        modifiedCount: modifiedFiles.length,
        allFileStates: allFiles.map(f => ({
          path: f.filePath, 
          gitState: f.gitState || this._migrateToGitState(f.state),
          previewState: f.previewState
        })),
        modifiedFileStates: modifiedFiles.map(f => ({
          path: f.filePath, 
          gitState: f.gitState || this._migrateToGitState(f.state)
        }))
      });
      
      return modifiedFiles;
    } catch (error) {
      console.error('Error getting modified files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get deleted files
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getDeletedFiles(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      return allFiles.filter(file => {
        const gitState = file.gitState || this._migrateToGitState(file.state);
        return gitState === GIT_STATE.DELETED;
      });
    } catch (error) {
      console.error('Error getting deleted files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get changed files for preview (files that need to be synced to live preview)
   * Returns files that haven't been sent to preview yet or are outdated
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getChangedFilesForPreview(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      // Return files that need preview sync: NOT_SENT or OUTDATED
      return allFiles.filter(file => {
        const previewState = file.previewState || this._migrateToPreviewState(file.state);
        return previewState === PREVIEW_STATE.NOT_SENT || 
               previewState === PREVIEW_STATE.OUTDATED;
      });
    } catch (error) {
      console.error('Error getting changed files for preview:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Mark file as sent to preview
   * Updates previewState to SENT without affecting gitState
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsPreviewSent(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      // Update preview state to SENT
      file.previewState = PREVIEW_STATE.SENT;
      file.lastSentToPreview = Date.now();
      
      // Keep gitState unchanged - preview sync is independent of git lifecycle

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          devLog(`✅ Marked as sent to preview: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as preview sent:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as preview sent:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Mark file as sent to GitLab (pushed)
   * Updates gitState to PUSHED without affecting previewState
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsSent(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      // Update git state to PUSHED
      file.gitState = GIT_STATE.PUSHED;
      file.lastPushedToGitLab = Date.now();
      
      // Keep previewState unchanged - git push is independent of preview lifecycle

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          devLog(`✅ Marked as sent to GitLab: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as sent:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as sent:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Mark all changed files as sent
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<void>}
   */
  async markAllChangedAsPreviewSent(projectId, serviceName) {
    try {
      const changedFiles = await this.getChangedFilesForPreview(projectId, serviceName);
      await Promise.all(
        changedFiles.map(file => this.markAsPreviewSent(projectId, serviceName, file.filePath))
      );
    } catch (error) {
      console.error('Error marking all as preview sent:', error); /* eslint-disable-line no-console */
    }
  }

  async markAllChangedAsSent(projectId, serviceName) {
    try {
      const changedFiles = await this.getChangedFilesForPreview(projectId, serviceName);
      await Promise.all(
        changedFiles.map(file => this.markAsSent(projectId, serviceName, file.filePath))
      );
    } catch (error) {
      console.error('Error marking all as sent:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Brings the OTHER scopes' cache entries of one project file in line with a change made through `serviceName`
   * (see cacheScopePaths.js: Home › Files caches `services/app/a.js` under '.', the service's code view under
   * 'services/app'). `change`: `{ deleted: true }` or `{ content, isBinary, mimeType, size }`, the file as it now is
   * on vfs-service. Only clean entries (ORIGINAL/PUSHED, or a pending DELETED once the file is gone) follow: an entry
   * with unsaved local work (NEW/MODIFIED) is never dropped or overwritten. vfs-backed projects only; announces the
   * scopes it changed with a `vfsCacheScopesChanged` event (and to other tabs) so open trees redraw.
   * @returns {Promise<string[]>} the scopes whose entry changed
   */
  async mirrorToOverlappingScopes(projectId, serviceName, filePath, change = {}) {
    try {
      if (!projectId || !serviceName || !filePath || !vfsClient.isVfsBackedProject(projectId)) { return []; }
      await this.init();
      const fullPath = fullPathOf(serviceName, filePath);
      const originId = this.generateFileId(projectId, serviceName, filePath);
      const changedScopes = [];
      for (const { scope, filePath: scopedPath } of scopesContaining(fullPath)) {
        const id = this.generateFileId(projectId, scope, scopedPath);
        if (id === originId || !scopedPath) { continue; }
        // eslint-disable-next-line no-await-in-loop
        const record = await this.getFile(projectId, scope, scopedPath);
        if (!record) { continue; }
        const state = record.gitState || this._migrateToGitState(record.state);
        const clean = state === GIT_STATE.ORIGINAL || state === GIT_STATE.PUSHED || (change.deleted && state === GIT_STATE.DELETED);
        if (!clean) { continue; }
        if (!change.deleted && record.content === change.content) { continue; }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve, reject) => {
          const transaction = this.db.transaction([FILES_STORE], 'readwrite');
          const store = transaction.objectStore(FILES_STORE);
          const request = change.deleted
            ? store.delete(id)
            : store.put({
              ...record,
              content: change.content,
              originalContent: change.content,
              gitState: GIT_STATE.ORIGINAL,
              isBinary: change.isBinary ?? record.isBinary,
              mimeType: change.mimeType || record.mimeType,
              size: change.size ?? record.size,
              lastModified: Date.now(),
            });
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        changedScopes.push(scope);
      }
      if (changedScopes.length) {
        announceCacheScopesChanged({ projectId, scopes: changedScopes, paths: [fullPath] });
      }
      return changedScopes;
    } catch (error) {
      console.warn('⚠️ Could not update the other scopes of', filePath, error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Mark file as deleted
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsDeleted(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      file.gitState = GIT_STATE.DELETED;
      file.lastModified = Date.now();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          devLog(`🗑️ Marked as deleted: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as deleted:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as deleted:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Delete file from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async deleteFile(projectId, serviceName, filePath, options = {}) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.delete(id);

        request.onsuccess = async () => {
          // eslint-disable-next-line no-console
          devLog(`🗑️ Deleted file from IndexedDB: ${filePath}`);
          // Gone here means gone on vfs-service for a vfs-backed project: the other trees' clean copies go too.
          if (!options.localOnly) {
            await this.mirrorToOverlappingScopes(projectId, serviceName, filePath, { deleted: true });
          }
          
          // Only dispatch event if not suppressed (e.g., during git pull)
          if (!options.suppressEvent) {
            // Dispatch event to notify Monaco editor and tree view of deletion
            const event = new CustomEvent('monacoFileChanged', {
              detail: {
                projectId,
                serviceName,
                filePath,
                deleted: true
              }
            });
            window.dispatchEvent(event);
          }
          
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to delete file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error deleting file:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Clear all files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} options - Options for clearing files
   * @returns {Promise<void>}
   */
  async clearProjectFiles(projectId, serviceName, options = {}) {
    try {
      const files = await this.getAllFiles(projectId, serviceName);
      await Promise.all(
        files.map(file => this.deleteFile(projectId, serviceName, file.filePath, options))
      );
      // eslint-disable-next-line no-console
      devLog(`🗑️ Cleared all files for ${projectId}/${serviceName}`);
    } catch (error) {
      console.error('Error clearing project files:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Check if there are any modified files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<boolean>}
   */
  async hasModifiedFiles(projectId, serviceName) {
    try {
      const modifiedFiles = await this.getModifiedFiles(projectId, serviceName);
      return modifiedFiles.length > 0;
    } catch (error) {
      console.error('Error checking for modified files:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get count of modified files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<number>}
   */
  async getModifiedFilesCount(projectId, serviceName) {
    try {
      const modifiedFiles = await this.getModifiedFiles(projectId, serviceName);
      return modifiedFiles.length;
    } catch (error) {
      console.error('Error getting modified files count:', error); /* eslint-disable-line no-console */
      return 0;
    }
  }

  /**
   * Check if file exists
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async fileExists(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    return !!file;
  }

  /**
   * Get file state
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<string|null>}
   */
  async getFileState(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    // Return a state object with both git and preview states
    if (!file) return null;

    return {
      gitState: file.gitState || GIT_STATE.MODIFIED,
      previewState: file.previewState || PREVIEW_STATE.NOT_SENT
    };
  }

  /**
   * Check if a file has local modifications
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async isFileModified(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    if (!file) return false;
    
    const gitState = file.gitState || this._migrateToGitState(file.state);
    return gitState === GIT_STATE.MODIFIED || gitState === GIT_STATE.NEW;
  }

  /**
   * Get all files for preview (merges original with modified)
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} originalFiles - Original files object {filePath: content}
   * @returns {Promise<object>} Combined files object {filePath: content}
   */
  async getAllFilesForPreview(projectId, serviceName, originalFiles = {}) {
    const allStoredFiles = await this.getAllFiles(projectId, serviceName);
    const allFiles = {...originalFiles};
    
    // Override with stored versions (prioritize modified, new, or different content)
    for (const fileData of allStoredFiles) {
      const gitState = fileData.gitState || this._migrateToGitState(fileData.state);
      if (gitState !== GIT_STATE.DELETED) {
        allFiles[fileData.filePath] = fileData.content;
      }
    }
    
    return allFiles;
  }

  /**
   * Save metadata (for project-level info)
   * @param {string} key
   * @param {any} value
   * @param {string} projectId
   * @returns {Promise<boolean>}
   */
  async saveMetadata(key, value, projectId = null) {
    try {
      await this.init();

      const data = {
        key,
        value,
        projectId,
        lastModified: Date.now()
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([METADATA_STORE], 'readwrite');
        const store = transaction.objectStore(METADATA_STORE);
        const request = store.put(data);

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to save metadata:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error saving metadata:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get metadata
   * @param {string} key
   * @returns {Promise<any|null>}
   */
  async getMetadata(key) {
    try {
      await this.init();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([METADATA_STORE], 'readonly');
        const store = transaction.objectStore(METADATA_STORE);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result?.value || null);
        };

        request.onerror = () => {
          console.error('❌ Failed to get metadata:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting metadata:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Get all files with their content for AI context
   * Merges original files from git with modified files from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} originalFiles - Original files from git {filePath: content}
   * @returns {Promise<object>} Combined files object {filePath: content}
   */
  async getFilesForAIContext(projectId, serviceName, originalFiles = {}) {
    try {
      const modifiedFiles = await this.getAllFiles(projectId, serviceName);
      const allFiles = {...originalFiles};
      
      // Override with modified versions from IndexedDB
      modifiedFiles.forEach(file => {
        allFiles[file.filePath] = file.content;
      });
      
      return allFiles;
    } catch (error) {
      console.error('Error getting files for AI context:', error); /* eslint-disable-line no-console */
      return originalFiles || {};
    }
  }

  /**
   * Migrate from localStorage fileStorageManager
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<number>} Number of files migrated
   */
  async migrateFromLocalStorage(projectId, serviceName) {
    try {
      const prefix = `monaco_file_${projectId}/${serviceName}/`;
      let migratedCount = 0;

      // Get all matching keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            const filePath = key.substring(prefix.length);

            await this.saveFile(
              projectId,
              serviceName,
              filePath,
              data.content,
              {
                originalContent: data.originalContent,
                branch: data.branch,
                metadata: data
              }
            );

            // Set correct state based on legacy data
            const file = await this.getFile(projectId, serviceName, filePath);
            if (file) {
              const legacyState = data.state || 'modified';
              // Migrate legacy state strings to new system
              switch (legacyState) {
                case 'original':
                  file.gitState = GIT_STATE.ORIGINAL;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'new':
                  file.gitState = GIT_STATE.NEW;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'deleted':
                  file.gitState = GIT_STATE.DELETED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'sent':
                  file.gitState = GIT_STATE.PUSHED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'preview_sent':
                  file.gitState = GIT_STATE.MODIFIED;
                  file.previewState = PREVIEW_STATE.SENT;
                  break;
                case 'modified_after_sent':
                  file.gitState = GIT_STATE.PUSHED;
                  file.previewState = PREVIEW_STATE.OUTDATED;
                  break;
                case 'modified':
                default:
                  file.gitState = GIT_STATE.MODIFIED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
              }
              await this.db.transaction([FILES_STORE], 'readwrite')
                .objectStore(FILES_STORE)
                .put(file);
            }

            localStorage.removeItem(key);
            migratedCount++;
          } catch (err) {
            console.error(`Failed to migrate ${key}:`, err); /* eslint-disable-line no-console */
          }
        }
      }

      if (migratedCount > 0) {
        // eslint-disable-next-line no-console
        devLog(`✅ Migrated ${migratedCount} files from localStorage to IndexedDB`);
      }

      return migratedCount;
    } catch (error) {
      console.error('Error migrating from localStorage:', error); /* eslint-disable-line no-console */
      return 0;
    }
  }

  /**
   * Helper: Convert base64 string to ArrayBuffer (for binary files)
   * @param {string} base64 - Base64 encoded string
   * @returns {ArrayBuffer}
   */
  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Helper: Convert base64 string to Blob (for binary files)
   * @param {string} base64 - Base64 encoded string
   * @param {string} mimeType - MIME type of the file
   * @returns {Blob}
   */
  base64ToBlob(base64, mimeType = 'application/octet-stream') {
    const arrayBuffer = this.base64ToArrayBuffer(base64);
    return new Blob([arrayBuffer], { type: mimeType });
  }

  /**
   * Helper: Get file as downloadable URL (for binary files)
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<string|null>} - Returns blob URL or null
   */
  async getFileAsUrl(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return null;

      if (file.isBinary && file.content) {
        // Check if content is base64-encoded or raw binary
        // file.encoding will be 'base64' if stored correctly from GitLab
        const isBase64 = file.encoding === 'base64' || /^[A-Za-z0-9+/=]+$/.test(file.content.substring(0, 100));
        
        // eslint-disable-next-line no-console
        devLog('🔧 Creating blob from:', {
          filePath,
          contentLength: file.content.length,
          encoding: file.encoding,
          isBase64,
          mimeType: file.mimeType,
          firstChars: file.content.substring(0, 30)
        });
        
        let blob;
        if (isBase64) {
          // Content is base64-encoded - this is the correct way
          // eslint-disable-next-line no-console
          devLog('📦 Using base64ToBlob converter');
          blob = this.base64ToBlob(file.content, file.mimeType);
          
          // eslint-disable-next-line no-console
          devLog('✅ Blob created from base64:', {
            size: blob.size,
            type: blob.type,
            base64Length: file.content.length,
            expectedBinarySize: Math.floor(file.content.length * 0.75) // base64 is ~33% larger than binary
          });
        } else {
          // Content is corrupted raw binary string - try to salvage it
          // eslint-disable-next-line no-console
          console.warn('⚠️ Content is corrupted raw binary string - attempting conversion');
          const bytes = new Uint8Array(file.content.length);
          for (let i = 0; i < file.content.length; i++) {
            bytes[i] = file.content.charCodeAt(i) & 0xFF; // Mask to 8 bits
          }
          blob = new Blob([bytes], { type: file.mimeType || 'application/octet-stream' });
          
          // eslint-disable-next-line no-console
          devLog('⚠️ Blob created from corrupted binary:', {size: blob.size, type: blob.type});
        }
        
        return URL.createObjectURL(blob);
      }

      // For text files, create a blob from text content
      const blob = new Blob([file.content], { type: 'text/plain' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating file URL:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Helper: Check if file is binary based on stored metadata
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async isBinaryFile(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      return file?.isBinary || false;
    } catch (error) {
      console.error('Error checking if file is binary:', error); /* eslint-disable-line no-console */
      return false;
    }
  }
}

// Export singleton instance
const indexedDbFileStorage = new IndexedDbFileStorage();

export {GIT_STATE, PREVIEW_STATE, EXCLUDED_FILES_FOR_AI, shouldExcludeFromAI};
export default indexedDbFileStorage;
